import { StandardEvents } from '@shopify/events';
import { formatMoney } from '@theme/money-formatting';

/**
 * Free-shipping progress bar. Re-reads the cart after every cart line update
 * and on back/forward cache restores, then updates the message and fill.
 */
class ShippingBar extends HTMLElement {
  #abortController = new AbortController();

  connectedCallback() {
    if (this.dataset.eligible !== 'true') return;

    const { signal } = this.#abortController;
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#onCartUpdate, { signal });
    window.addEventListener('pageshow', this.#onPageShow, { signal });

    // Presentment currency differs from shop currency: Liquid can't convert the threshold, so do it here.
    if (this.#rate !== 1) this.#refresh();
  }

  disconnectedCallback() {
    this.#abortController.abort();
  }

  get #rate() {
    const rate = Number(window.Shopify?.currency?.rate);
    return Number.isFinite(rate) && rate > 0 ? rate : 1;
  }

  /** @param {Event & { promise?: Promise<unknown> }} event */
  #onCartUpdate = async (event) => {
    try {
      await event.promise;
    } catch {
      return;
    }
    this.#refresh();
  };

  /** @param {PageTransitionEvent} event */
  #onPageShow = (event) => {
    if (event.persisted) this.#refresh();
  };

  async #refresh() {
    try {
      const response = await fetch(`${window.Shopify?.routes?.root ?? '/'}cart.js`, {
        signal: this.#abortController.signal,
        headers: { Accept: 'application/json' },
      });
      if (!response.ok) return;
      const cart = await response.json();
      this.#render(cart.total_price, cart.currency);
    } catch {
      // Network failure or aborted: keep the server-rendered state.
    }
  }

  /**
   * @param {number} total - Cart total in minor units of the presentment currency
   * @param {string} currency - Presentment currency code
   */
  #render(total, currency) {
    const message = this.querySelector('.shipping-bar__message');
    const fill = /** @type {HTMLElement | null} */ (this.querySelector('.shipping-bar__fill'));
    if (!message) return;

    const threshold = Math.round(Number(this.dataset.threshold) * this.#rate);
    const format = this.dataset.moneyFormat || '${{amount}}';
    const money = (/** @type {number} */ value) => formatMoney(value, format, currency);
    const remaining = threshold - total;

    if (total <= 0) {
      message.textContent = (this.dataset.textEmpty ?? '').replace('[amount]', money(threshold).replace(/\.00$/, ''));
    } else if (remaining > 0) {
      const [before = '', after = ''] = (this.dataset.textAway ?? '').split('[amount]');
      const strong = document.createElement('strong');
      strong.textContent = money(remaining);
      message.replaceChildren(before, strong, after);
    } else {
      message.textContent = this.dataset.textUnlocked ?? '';
    }

    if (fill) {
      const progress = Math.max(0, Math.min(100, Math.floor((total / threshold) * 100)));
      fill.style.setProperty('--shipping-progress', `${progress}%`);
    }
  }
}

if (!customElements.get('shipping-bar')) {
  customElements.define('shipping-bar', ShippingBar);
}
